import React, { useState } from 'react';
import { ChevronRight, MoreHorizontal, X } from 'lucide-react';
import RowActionsMenu from './RowActionsMenu';

// Collapsible section rendered as its own card — piloted on the Commercial
// Entity detail page's left/right panels (grouping Classification/Addresses/
// etc.), reusable anywhere a record-detail page groups display fields under
// a toggleable heading. Each instance is a standalone white card (HubSpot's
// company-record layout: Address Details/Categories/etc. each get their own
// card rather than sharing one long panel) — the marginBottom below is what
// creates the gap between stacked cards, so a consuming page just renders
// them one after another with no wrapping card/gap of its own.
// `headerExtra`: interactive content (a clickable pill, an icon button)
// that needs to sit in the header but outside the toggle — `title` renders
// inside a native <button>, so a nested interactive element there would be
// invalid HTML and would also toggle the accordion on every click. Always
// rendered (independent of menuItems/onDismiss, which stay the mutually-
// exclusive right-edge slot), and stops its own clicks from bubbling up to
// nothing in particular, just for clarity that it's a separate hit target.
// `open`/`onToggle`: optional controlled pair for a caller that needs to
// force the panel open itself (e.g. CommercialEntityDetailPage.js expanding
// Addresses/Emails/Phones when their "Add" menu item is picked) — omit both
// and the accordion manages its own open state exactly as before.
// `className`: on the card, for a page's own CSS to reach its header row
// (.accordion-header) and headerExtra (.accordion-header-extra), e.g.
// NextStepButton.css's "doc-header" wrapping on a narrow screen.
// `showMenuPlaceholder={false}`: leaves the right-edge slot empty instead of
// the static placeholder icon, for screens with no per-card actions at all
// (the customer portal's account page).
function Accordion({ title, defaultOpen = true, open: openProp, onToggle, menuItems, onDismiss, headerExtra, showMenuPlaceholder = true, className, children }) {
  const [openState, setOpenState] = useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : openState;
  const toggle = () => (isControlled ? onToggle() : setOpenState((o) => !o));
  return (
    <div className={className} style={{ background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, marginBottom: 12, padding: '0 16px' }}>
      <div className="accordion-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <button
          type="button"
          onClick={toggle}
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'none',
            border: 'none',
            padding: '18px 0',
            cursor: 'pointer',
            fontSize: 15,
            fontWeight: 500,
            color: '#1a1a1a',
            textAlign: 'left',
          }}
        >
          <ChevronRight
            size={18}
            strokeWidth={2}
            style={{ color: '#9aa5b5', flexShrink: 0, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}
          />
          {title}
        </button>
        {headerExtra && (
          <div className="accordion-header-extra" onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {headerExtra}
          </div>
        )}
        {menuItems ? (
          <RowActionsMenu items={menuItems} ariaLabel={`${title} actions`} />
        ) : onDismiss ? (
          // Dismisses the whole card (e.g. SalesOrderTimeline.js/
          // QuoteTimeline.js) rather than just collapsing it — the caller
          // owns whether/how that state resets.
          <button
            type="button"
            onClick={onDismiss}
            title={`Hide ${title}`}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0, color: '#9aa5b5' }}
          >
            <X size={18} strokeWidth={2} />
          </button>
        ) : showMenuPlaceholder && (
          // Placeholder for a future per-section Actions/settings affordance
          // (HubSpot's company-record cards each have one) — static and
          // non-interactive on purpose, since there's no real menu behind it
          // yet and a clickable-looking control that does nothing is worse
          // than none at all. Swap for a real menu once one exists.
          <MoreHorizontal size={18} strokeWidth={2} style={{ color: '#c3cad6', flexShrink: 0 }} />
        )}
      </div>
      {open && <div style={{ paddingBottom: 16 }}>{children}</div>}
    </div>
  );
}

export default Accordion;
